import { useCallback, useMemo, useState } from "react";
import { AlertMessageType } from "../Alert";
import { AssertType } from "../@types/AssertType";
import { UnionToIntersection } from "../@types/UnionToIntersection";
import { CompleteScreen } from "./screens/CompleteScreen";
import { DownloadCreateTemplateScreen } from "./screens/DownloadCreateTemplateScreen";
import { DownloadUpdateTemplateScreen } from "./screens/DownloadUpdateTemplateScreen";
import { LoadCSVScreen } from "./screens/LoadCSVScreen";
import { CSVImportUIScreenTitles, ScreenId } from "./screens/types";
import { ScreenProvider, useScreen } from "./ScreenProvider";
import { SelectModeScreen } from "./screens/SelectModeScreen";
import { UploadCSVScreen } from "./screens/UploadCSVScreen";

export type { CSVImportUIScreenTitles };

export type Mode = "create" | "update";

export type CellError = [string, string];

export type CSVImportUIColumn = Readonly<{
  label: string;
  description?: string;
  type?: "date" | "number";
  options?: readonly string[];
  required?: boolean;
  hiddenOnCreateTemplate?: boolean;
  /**
   * アップロードされたCSVにヘッダーがなければ表示しない。
   */
  hiddenIfNoHeader?: boolean;
  renderCell?: (props: {
    value: string;
    header: string;
    row: Record<string, string>;
  }) => React.JSX.Element;
}>;

/**
 * ライブラリ利用者用の型
 */
export type CSVImportUIColumns = Record<string, CSVImportUIColumn>;

export interface CSVImportUIRenderSearchFieldProps<Row> {
  onChange: (args: Row[]) => void;
}

type OptionByLabel<Columns extends CSVImportUIColumns> = {
  [Key in keyof Columns]: Record<
    Columns[Key]["label"],
    Columns[Key] extends {
      options: readonly (infer Option)[];
    }
      ? Option extends string
        ? Option
        : never
      : string
  >;
}[keyof Columns];

export type CSVImportUIRow<Columns extends CSVImportUIColumns> = AssertType<
  UnionToIntersection<OptionByLabel<Columns>>,
  Record<string, string>
>;

export interface CSVImportUIProps<
  Columns extends CSVImportUIColumns,
  Row extends Record<string, string> = CSVImportUIRow<Columns>,
> {
  /**
   * 利用できるモードを指定する。
   */
  acceptMode: Mode[];
  /**
   * アップロード画面に進む前にトリガーされる。
   * 行ごとにデータを処理する。
   *
   * onPreUploadDataが指定されている場合、こちらは無視される。
   *
   * この関数内でsetStateを使用すると、行ごとの処理で過剰なレンダリングを引き起こす可能性があります。
   * 状態更新が必要な場合は、以下の方法を検討してください：
   * - onPreUploadDataを使用して一括でデータを処理する
   * - 複数の更新をバッチ処理して、すべての行の処理後に一度だけ状態を更新する
   * - レンダリングを必要としない中間値にはuseRefを使用する
   */
  onPreUploadDataByRow?: (args: {
    index: number;
    /**
     * 欠損したフィールドがある可能性があるため、型を緩くする。
     */
    row: Partial<Row>;
    mode: Mode;
  }) => Promise<{
    /**
     * @deprecated 代わりに `errors` を使用してください。
     */
    error?: CellError;
    errors?: CellError[];
    data?: Record<string, string>;
  }>;
  /**
   * アップロード画面に進む前にトリガーされる。
   * 一括でデータをアップロードする。
   *
   * 現状onPreUploadDataByRowByRowとは異なり、行ごとにエラーを表示することはできない。
   * 代わりに、エラー時に表示されるアラートのメッセージをerrorMessageで指定する。
   *
   * 行毎にエラーを表示したくなった場合は、要望を出すこと。
   */
  onPreUploadData?: (args: { data: Partial<Row>[]; mode: Mode }) => Promise<{
    errorMessage?: AlertMessageType;
    data?: Record<string, string>[];
  }>;
  /**
   * CSVをパースしたデータのアップロードがトリガーされた際に呼ばれる。
   * 行ごとにデータをアップロードする。
   *
   * onUploadDataが指定されている場合、こちらは無視される。
   *
   * この関数内でsetStateを使用すると、行ごとの処理で過剰なレンダリングを引き起こす可能性があります。
   * 状態更新が必要な場合は、以下の方法を検討してください：
   * - onUploadDataを使用して一括でデータを処理する
   * - 複数の更新をバッチ処理して、すべての行の処理後に一度だけ状態を更新する
   * - レンダリングを必要としない中間値にはuseRefを使用する
   */
  onUploadDataByRow?: (args: {
    index: number;
    /**
     * 欠損したフィールドがある可能性があるため、型を緩くする。
     */
    row: Partial<Row>;
    mode: Mode;
  }) => Promise<{
    /**
     * @deprecated 代わりに `errors` を使用してください。
     */
    error?: CellError;
    errors?: CellError[];
    data?: Record<string, string>;
  }>;
  /**
   * CSVをパースしたデータのアップロードがトリガーされた際に呼ばれる。
   * 一括でデータをアップロードする。
   *
   * 現状onUploadDataByRowとは異なり、行ごとにエラーを表示することはできない。
   * 代わりに、エラー時に表示されるアラートのメッセージをerrorMessageで指定する。
   *
   * 行毎にエラーを表示したくなった場合は、要望を出すこと。
   */
  onUploadData?: (args: { data: Partial<Row>[]; mode: Mode }) => Promise<{
    errorMessage?: AlertMessageType;
    data?: Record<string, string>[];
  }>;
  /**
   * 検索フォームのラベルとして利用される。
   * (only upload mode)
   */
  searchLabel?: string;
  /**
   * 検索が行われた際にトリガーされる。
   * ユーザーの入力した文字列に応じてデータを返却する。このデータはCSVとしてダウンロードされる。
   * (only upload mode)
   */
  onSearchData?: (args: { value: string }) => Promise<{
    data: Row[];
  }>;
  /**
   * CSVの各列をどのように表示するか指定する。
   * 各列には、次のプロパティが指定できる。
   *
   * `label` : CSVのヘッダーとして利用される。
   *
   * `type` : `date`または`number`が指定でき、それぞれ日付と数値としてアップロードされたCSVの値が正しいかどうかを検証する。
   *
   * `options` : その列が持てる値のリストを指定する。アップロードされたCSVの値がこのリストに含まれているかどうかを検証する。
   *
   * `required` : その列が必須かどうかを指定する。アップロードされたCSVの値が空でないかどうかを検証する。
   *
   * `renderCell` : その列のセルをどのように表示するかを指定する。その列の値、ヘッダー、行のデータが渡される。
   *
   * `hiddenOnCreateTemplate` : 作成用のCSVのテンプレートに、その列を表示しないかどうかを決定する。
   *
   * `hiddenIfNoHeader` : アップロードされたCSVにそのヘッダーがなければ、その列を表示しないかどうかを決定する。
   */
  columns: Columns;
  /**
   * ダウンロードされるCSVに付与されるプレフィックスを指定する
   */
  csvPrefix?: string;
  /**
   * CSVのダウンロードがトリガーされたときに呼ばれる
   */
  onDownloadCSV: (args: { data: Row[]; filename: string }) => void;
  /**
   * CSVがアップロードされたときに呼ばれる
   */
  csvParser: (args: {
    file: File;
  }) => Promise<{ data: Record<string, string>[] }>;
  /**
   * アップロードされたCSVのデータをバリデーションする。
   */
  csvValidator?: (args: {
    /**
     * ここで渡されるデータがRowと一致するとは限らない。
     */
    row: Record<string, string>;
  }) => Promise<{ errors?: CellError[] }>;
  /**
   * 更新モードで検索のカスタマイズをする。
   * 省略するとonSearchData, searchLabelを用いた単一の検索フィールドが表示される。これを指定するとonSearchData, searchLabelは無視される。
   * (only upload mode)
   */
  renderSearchField?: (
    props: CSVImportUIRenderSearchFieldProps<Row>,
  ) => React.JSX.Element;
  /**
   * 編集用CSVをダウンロードするページをスキップする
   */
  skipDownloadTemplate?: boolean;
  /**
   * 更新用のCSVをダウンロードする際にデフォルトで表示されるデータを指定する。
   */
  defaultTemplateData?: Row[];

  /**
   * ダウンロードテンプレートで利用する項目を選択するかどうかを指定する。
   * デフォルトでは、hiddenOnCreateTemplateが指定されていない列の全ての項目が追加される。
   * @default false
   */
  selectDownloadTemplateColumns?: boolean;
  /**
   * 各スクリーンのタイトルを上書きする。
   *
   * CSVImportUIは作成・更新を主な用途としているため、デフォルトのタイトルには
   * 「作成」「更新」「アップロード」といった文言が含まれる。
   * それ以外の用途で利用する場合は、このpropで適切なタイトルに差し替えることを推奨する。
   *
   * @example
   * ```tsx
   * screenTitles={{
   *   selectMode: "操作の選択",
   *   downloadCreateTemplate: "登録用CSVのダウンロード",
   *   downloadUpdateTemplate: "編集用CSVのダウンロード",
   *   loadCSV: "CSVの読み込み",
   *   uploadCSV: "実行",
   *   complete: "完了しました",
   * }}
   * ```
   */
  screenTitles?: CSVImportUIScreenTitles;
}

const CSVImportUIContent = <
  Columns extends CSVImportUIColumns,
  Row extends Record<string, string> = CSVImportUIRow<Columns>,
>({
  columns,
  onDownloadCSV,
  searchLabel,
  onSearchData,
  renderSearchField,
  csvPrefix,
  csvParser,
  csvValidator,
  onPreUploadData,
  onPreUploadDataByRow,
  onUploadData,
  onUploadDataByRow,
  acceptMode,
  skipDownloadTemplate,
  selectDownloadTemplateColumns,
  defaultTemplateData,
  screenTitles,
}: CSVImportUIProps<Columns, Row>) => {
  const id = useScreen();
  const [mode, setMode] = useState<Mode>(acceptMode[0]);
  const [uploadedData, setUploadedData] = useState<Row[]>([]);
  const [errors, setErrors] = useState<(CellError[] | undefined)[]>([]);

  const handleUpdateData = useCallback(
    (data: Row[], errors: (CellError[] | undefined)[]) => {
      setErrors(errors);
      setUploadedData(data);
    },
    [],
  );

  const headers = useMemo(() => {
    const rowKeys = Object.keys(uploadedData[0] ?? {});

    return Object.values(columns)
      .filter((column) => {
        if (rowKeys.length === 0) {
          return true;
        }
        if (column.hiddenIfNoHeader && !rowKeys.includes(column.label)) {
          return false;
        }
        return true;
      })
      .map((column) => column.label);
  }, [columns, uploadedData]);

  const filledData = useMemo(() => {
    return headers.length > Object.keys(uploadedData[0] ?? {}).length
      ? uploadedData.map((row) => {
          return {
            ...Object.fromEntries(headers.map((header) => [header, ""])),
            ...row,
          };
        })
      : uploadedData;
  }, [headers, uploadedData]);

  switch (id) {
    case "selectMode": {
      return (
        <SelectModeScreen
          skipDownloadTemplate={skipDownloadTemplate}
          onChangeMode={setMode}
          title={screenTitles?.selectMode}
        />
      );
    }
    case "downloadCreateTemplate": {
      return (
        <DownloadCreateTemplateScreen
          onDownloadCSV={onDownloadCSV}
          headers={headers}
          columns={columns}
          csvPrefix={csvPrefix}
          selectDownloadTemplateColumns={selectDownloadTemplateColumns}
          title={screenTitles?.downloadCreateTemplate}
        />
      );
    }
    case "downloadUpdateTemplate": {
      return (
        <DownloadUpdateTemplateScreen
          searchLabel={searchLabel}
          onSearch={onSearchData}
          renderSearchField={renderSearchField}
          onDownloadCSV={onDownloadCSV}
          csvPrefix={csvPrefix}
          headers={headers}
          defaultTemplateData={defaultTemplateData}
          columns={columns}
          title={screenTitles?.downloadUpdateTemplate}
        />
      );
    }
    case "loadCSV": {
      return (
        <LoadCSVScreen
          mode={mode}
          columns={columns}
          data={filledData}
          headers={headers}
          errors={errors}
          csvParser={csvParser}
          csvValidator={csvValidator}
          onPreUploadData={onPreUploadData}
          onPreUploadDataByRow={onPreUploadDataByRow}
          onUpdateData={handleUpdateData}
          title={screenTitles?.loadCSV}
        />
      );
    }
    case "uploadCSV": {
      return (
        <UploadCSVScreen
          mode={mode}
          data={filledData}
          headers={headers}
          errors={errors}
          columns={columns}
          onUploadData={onUploadData}
          onUploadDataByRow={onUploadDataByRow}
          onSubmitComplete={handleUpdateData}
          title={screenTitles?.uploadCSV}
        />
      );
    }
    case "complete": {
      return (
        <CompleteScreen
          csvPrefix={csvPrefix}
          onDownloadCSV={onDownloadCSV}
          data={filledData}
          columns={columns}
          headers={headers}
          errors={errors}
          title={screenTitles?.complete}
        />
      );
    }
    default: {
      return null;
    }
  }
};

const getFirstScreenId = (
  accptMode: Mode[],
  skipDownloadTemplate?: boolean,
): ScreenId => {
  if (accptMode.length > 1) {
    return "selectMode";
  }
  if (skipDownloadTemplate) {
    return "loadCSV";
  }
  if (accptMode[0] === "create") {
    return "downloadCreateTemplate";
  }
  if (accptMode[0] === "update") {
    return "downloadUpdateTemplate";
  }
  return "selectMode";
};

export const CSVImportUI = <
  Columns extends CSVImportUIColumns,
  Row extends Record<string, string> = CSVImportUIRow<Columns>,
>(
  props: CSVImportUIProps<Columns, Row>,
) => {
  return (
    <ScreenProvider
      firstScreenId={getFirstScreenId(
        props.acceptMode,
        props.skipDownloadTemplate,
      )}
    >
      <CSVImportUIContent {...props} />
    </ScreenProvider>
  );
};
